import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const AUTH_IMG = "/auth-illustration.png";

export default function Register() {
    const { register } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
    const [submitting, setSubmitting] = useState(false);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await register(form);
            toast.success(t("toast_account_created"));
            nav("/app/shops");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("toast_register_failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="auth-split">
            <div className="flex flex-col px-6 py-8 lg:px-16 lg:py-12 bg-white">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00153f] mb-12" data-testid="back-home">
                    <ArrowLeft className="w-4 h-4" /> {t("back")}
                </Link>
                <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500 mb-4">
                        <ShieldCheck className="w-3.5 h-3.5" /> EU 2023/2673
                    </div>
                    <h1 className="font-display text-4xl font-bold tracking-tight text-[#00153f]">{t("register_title")}</h1>
                    <p className="text-gray-600 mt-2">{t("register_sub")}</p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5">
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("name")}</Label>
                            <Input data-testid="reg-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} className="mt-2 h-11" />
                        </div>
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("email")}</Label>
                            <Input data-testid="reg-email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className="mt-2 h-11" />
                        </div>
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("password")}</Label>
                            <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={(e) => setField("password", e.target.value)} className="mt-2 h-11" />
                        </div>
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("company")}</Label>
                            <Input data-testid="reg-company" value={form.company} onChange={(e) => setField("company", e.target.value)} className="mt-2 h-11" />
                        </div>
                        <Button data-testid="register-submit" disabled={submitting} type="submit" className="w-full h-11 bg-[#0b905d] hover:bg-[#067a4d]">
                            {submitting ? "..." : t("register_submit")}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-gray-600">
                        {t("already_account")}{" "}
                        <Link to="/login" className="text-[#00153f] font-semibold underline underline-offset-2" data-testid="link-login">{t("nav_login")}</Link>
                    </p>
                </div>
            </div>
            <div className="auth-visual">
                <img src={AUTH_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#00153f]/60 to-[#00153f]/20" />
                <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
                    <div className="text-xs font-mono uppercase tracking-widest text-white/70 mb-2">In 30 Sekunden startklar</div>
                    <h2 className="font-display text-3xl font-bold leading-tight max-w-md">
                        Erstellen Sie Ihren Account und generieren Sie Ihren ersten Widerrufslink.
                    </h2>
                </div>
            </div>
        </div>
    );
}
